
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MIcon from "@/components/MIcon.vue";
import MTextField from "@/components/form/MTextField.vue";
import MMultiSelectBox from "@/components/form/MMultiSelectBox.vue";
import store from "@/store";
import { Options, Vue } from "vue-class-component";
import { Classroom } from "@/entities/classroom";
import { TutorMeta } from "@/api/tutor";
import {
  deleteClassroom,
  getClassRoomsBasedOnRole,
  updateClassroom
} from "@/api/classroom";
import { saveErrorLog } from "@/api/error";
import { Getter } from "@/store/helper";
import { MAX_CONCURRENT_STUDENT_DELETES } from "@/constants/student";

@Options({
  components: {
    MBaseModal,
    MButton,
    MIcon,
    MTextField,
    MMultiSelectBox
  },
  emits: ["close"],
  props: {
    classroom: Object,
    tutors: Array
  }
})
export default class MCEditModal extends Vue {
  classroom!: Classroom;
  name = "";
  id = "";
  selectTutorIds: string[] = [];
  tutors: TutorMeta[] = [];

  @Getter("isServiceProvider") isServiceProvider!: boolean;

  get isSchool(): boolean {
    return store.state.isSchool;
  }

  get tutorsExceptAdminAndOwner(): TutorMeta[] {
    return this.tutors.filter(item =>
      ["general", "employee", "supervisor"].includes(item.config.data.authority)
    );
  }

  get selectTutors(): TutorMeta[] {
    return this.tutorsExceptAdminAndOwner.filter(item =>
      this.selectTutorIds.includes(item.main.ref.id)
    );
  }

  get tutorsPlaceholder(): string {
    let text = `${this.selectTutors.length}人`;
    if (this.selectTutors.length === 0) {
      return text;
    }
    text += ": ";
    this.selectTutors.forEach(tutor => {
      text += `${tutor.main.data.name},`;
    });

    return text.substring(0, text.length - 1);
  }

  get selectedClassRoomSchoolId(): string {
    return this.classroom.ref.parent.parent!.id;
  }

  get validName() {
    return this.name.length > 0;
  }

  get validId() {
    const classRooms: Classroom[] = getClassRoomsBasedOnRole(
      this.isServiceProvider,
      store.state.classrooms,
      this.selectedClassRoomSchoolId
    );
    if (
      classRooms
        .filter(item => item.ref.id !== this.classroom.ref.id)
        .map(item => item.data.id)
        .includes(this.id)
    ) {
      return false;
    }
    return /^[0-9a-zA-Z]{2,}$/.test(this.id);
  }

  get validData() {
    return this.validName && this.validId;
  }

  changeTutor(id: string) {
    if (this.selectTutorIds.includes(id)) {
      this.selectTutorIds = this.selectTutorIds.filter(item => item !== id);
    } else {
      this.selectTutorIds.push(id);
    }
  }

  async update() {
    if (!this.validData) {
      return;
    }

    store.commit("START_LOADING", "変更内容保存中...");

    const classroomId = this.classroom.ref.id;
    const schoolId = this.classroom.ref.parent.parent!.id;
    try {
      await updateClassroom(schoolId, classroomId, {
        id: this.id,
        name: this.name,
        tutors: this.selectTutors.map(item => item.main.ref.id)
      });
      store.commit("END_LOADING");
    } catch (e) {
      store.commit("END_LOADING");
      alert(e);
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to update classroom"
      );
      return;
    }

    this.$router.go(0);
  }

  async deleteClassroom() {
    const confirmRes = confirm(
      "教室と所属生徒を本当に削除しますか？一度削除した教室・所属生徒を復元することはできません。"
    );
    if (!confirmRes) {
      return;
    }

    store.commit("START_LOADING", "削除中...");
    try {
      await deleteClassroom(this.classroom.ref);
      store.commit("END_LOADING");

      if (this.classroom.data.studentCount > MAX_CONCURRENT_STUDENT_DELETES) {
        alert(
          "教室を削除しました。" +
            "\n" +
            "所属生徒の情報が完全に削除されるまでに数分～数時間がかかる場合があります。"
        );
      }
    } catch (e) {
      store.commit("END_LOADING");
      alert(e);
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to delete classroom"
      );
      return;
    }

    this.$router.go(0);
  }

  close() {
    this.$emit("close");
  }

  created() {
    this.name = this.classroom.data.name;
    this.id = this.classroom.data.id;
    this.selectTutorIds = this.tutors
      .filter(
        tutor =>
          this.classroom.data.tutors.includes(tutor.main.ref.id) &&
          ["general", "employee", "supervisor"].includes(
            tutor.config.data.authority
          )
      )
      .map(item => item.main.ref.id);
  }
}
